import type { RumEvent } from "@datadog/browser-rum";

/**
 * Caps identical RUM error messages per app load: the first `maxOccurrences`
 * pass and the rest are dropped for the session. There's no time window, so a
 * user stuck on a failing page can't keep re-earning the allowance.
 * Keep in sync with pro-web's `datadog-error-throttle.util.ts`.
 */
export interface DuplicateErrorThrottleOptions {
  /** Copies of a message allowed per app load. Defaults to 10. */
  readonly maxOccurrences?: number;
  /** Distinct messages tracked before the counts reset. Defaults to 500. */
  readonly maxTrackedSignatures?: number;
}

const DEFAULT_MAX_OCCURRENCES = 10;
const DEFAULT_MAX_TRACKED_SIGNATURES = 500;
const MAX_SIGNATURE_LENGTH = 500;

/** Returns a stateful predicate that is `true` when an error should be dropped. */
export function createDuplicateErrorThrottle(
  options: DuplicateErrorThrottleOptions = {},
): (event: RumEvent | null | undefined) => boolean {
  const maxOccurrences = options.maxOccurrences ?? DEFAULT_MAX_OCCURRENCES;
  const maxTracked =
    options.maxTrackedSignatures ?? DEFAULT_MAX_TRACKED_SIGNATURES;

  const counts = new Map<string, number>();

  return function isThrottledDuplicateError(
    event: RumEvent | null | undefined,
  ): boolean {
    if (event?.type !== "error") {
      return false;
    }

    const message = event.error?.message;
    if (!message) {
      return false;
    }

    const signature = message.slice(0, MAX_SIGNATURE_LENGTH);
    const count = counts.get(signature) ?? 0;

    if (count >= maxOccurrences) {
      return true;
    }

    // Bound memory: a flood of distinct messages resets all counts.
    if (count === 0 && counts.size >= maxTracked) {
      counts.clear();
    }

    counts.set(signature, count + 1);
    return false;
  };
}
